import { faFlag, faRobot, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DOMPurify from 'dompurify';
import { FormEvent, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Gravatar from 'react-awesome-gravatar';
import { Alert, Badge, Button, Form, Image, OverlayTrigger, Popover } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import { useDetectChanges } from '../../../hooks/useDetectChanges';
import { canAccessFeature } from '../../utils/utils';
import { WithTooltip } from '../WithTooltip/WithTooltip';
import styles from './Chatbot.module.scss';

const ChatbotComponent = () => {
  const buttonRef = useRef<HTMLButtonElement>();
  const textRef = useRef<HTMLTextAreaElement>();
  const containerRef = useRef<HTMLDivElement>();
  const [warningAccepted, setWarningAccepted] = useState(false);
  const [text, setText] = useState('');
  const { userDataFmt, currentUser, subscription, staticData, isSubscriptionCheckedOnce } = useFirebase();
  const [loading, setLoading] = useState(true);
  const [thinking, setThinking] = useState(true);
  const [messages, setMessages] = useState<any[]>([]);
  const [sessionId, setSessionId] = useState<string>(null);
  const detectChanges = useDetectChanges();
  const ranOnce = useRef(false);
  const sessionCreated = useRef(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const { pathname } = useLocation();
  const { currentLanguage } = useCurrentLanguage();
  const maxTextLength = 500;

  const canUseChatbot = useMemo(() => {
    return canAccessFeature('CHATBOT', currentLanguage, staticData, userDataFmt);
  }, [staticData, currentLanguage, userDataFmt]);

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  const baseUrl = (() => {
    if (globalThis.location.hostname == 'localhost') return 'http://localhost:5050/chat';
    if (globalThis.location.origin.includes('tootor-dev') || globalThis.location.origin.includes('tootor-stage') || process.env.NODE_ENV == 'development') {
      return 'https://tootor-api.exacloud.it/chat';
    }
    return 'https://api.tootor.it/chat';
  })();

  const converse = useCallback(
    async (proceed: boolean, messages: any[], sessionId: string, text?: string, additionalParams: Record<string, string> = {}) => {
      if (!proceed) {
        setThinking(false);
        detectChanges();
        scrollToBottom();
        return;
      }
      setThinking(true);
      await new Promise((resolve) => setTimeout(resolve, 250));
      const message = { source: 'bot', text: '', thinking: true, id: Date.now(), error: false };
      setMessages([...messages, message]);
      const url = new URL(`${baseUrl}/sessions/${sessionId}/converse`);
      url.searchParams.set('token', await currentUser.getIdToken(true));
      if (text) url.searchParams.set('message', text.slice(0, maxTextLength));
      for (const [key, value] of Object.entries(additionalParams)) {
        url.searchParams.set(key, value);
      }
      const eventSource = new EventSource(url);
      eventSource.onmessage = (event) => {
        message.thinking = false;
        if (event.data.trim() == '--END--') {
          setThinking(false);
          eventSource.close();
        } else {
          message.text += event.data;
          message.id = Date.now();
        }
        detectChanges();
        scrollToBottom();
      };
      let retryCount = 0;
      const MAX_RETRIES = 2;
      eventSource.onerror = () => {
        if (eventSource.readyState === EventSource.CLOSED || retryCount >= MAX_RETRIES) {
          message.error = true;
          message.thinking = false;
          setThinking(false);
          eventSource.close();
          detectChanges();
          scrollToBottom();
        } else {
          retryCount++;
          message.text = '';
          message.thinking = true;
          detectChanges();
        }
      };
    },
    [messages]
  );

  const closeSession = async () => {
    const token = await currentUser.getIdToken(true);
    await fetch(`${baseUrl}/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: { authorization: token },
    });
    setSessionId(null);
    setLoading(true);
    setThinking(true);
    setSessionExpired(false);
    setMessages([]);
    sessionCreated.current = false;
    ranOnce.current = false;
    detectChanges();
  };

  useEffect(() => {
    if (!currentUser) return;
    if (!userDataFmt) return;
    if (!canUseChatbot) return;
    if (!isSubscriptionCheckedOnce) return;
    if (ranOnce.current) return;
    ranOnce.current = true;
    currentUser
      .getIdToken(true)
      .then((token) => {
        return fetch(`${baseUrl}/sessions`, {
          method: 'POST',
          headers: { authorization: token, 'content-type': 'application/json' },
          body: JSON.stringify({
            email: currentUser.email,
            job: userDataFmt.profile.formData.categoriaProfessionale,
            subscriptionActive: (+!!subscription).toString(),
            title: 'Doctor',
            firstName: userDataFmt.profile.formData.nome,
            lastName: userDataFmt.profile.formData.cognome,
            language: userDataFmt.preferredLanguage,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        });
      })
      .then((res) => res.json())
      .then(async ({ sessionId, messages }) => {
        setLoading(false);
        setSessionId(sessionId);
        setMessages(messages);
        sessionCreated.current = true;

        const url = new URL(`${baseUrl}/sessions/${sessionId}/events`);
        url.searchParams.set('token', await currentUser.getIdToken(true));
        const eventSource = new EventSource(url);
        eventSource.onmessage = (event) => {
          if (event.data == 'exp') {
            setSessionExpired(true);
            eventSource.close();
          }
        };

        return converse(!messages.length, messages, sessionId);
      })
      .then(() => {});
  }, [currentUser, userDataFmt, ranOnce.current, canUseChatbot, subscription, isSubscriptionCheckedOnce]);

  const close = () => {
    setText('');
    buttonRef.current.click();
  };

  useEffect(() => {
    if (!thinking) {
      textRef.current?.focus();
    }
  }, [thinking]);

  useEffect(() => {
    scrollToBottom();

    if (!sessionCreated.current) return;

    const last = messages[messages.length - 1];
    if (!last || last.source == 'bot') return;

    const additionalOptions: Record<string, string> = {};

    if (pathname.includes('/watch')) {
      try {
        additionalOptions.currentlyWatching = pathname.split('/watch/')[1].split('?')[0];
      } catch {}
    }

    converse(true, messages, sessionId, last.text, additionalOptions);
  }, [messages, sessionCreated.current]);

  const onFormSubmit = (e?: FormEvent<HTMLFormElement>, txt?: string) => {
    e?.preventDefault();
    const _text = (txt ?? text)?.trim();
    if (!_text) return;
    if (thinking) return;
    const userMessage = { source: 'user', text: _text, id: Date.now() };
    setMessages([...messages, userMessage]);
    setText('');
  };

  const parse = (text: string) => {
    return (
      // Sanitize the whole text, keeping only the allowed tags
      DOMPurify.sanitize(text, { ALLOWED_TAGS: ['b', 'br', 'ul', 'ol', 'li', 'a'] })
        // Remove every < and > that are not part of a <br> tag
        // .replaceAll(/<(?!br>)|(?<!<br)>/gi, '')
        // Extracts the link from a [Markdown](link)
        // .replaceAll(/\[.+?]\((.+?)\)/g, (_, $1) => `<rawlink>${$1}</rawlink>`)
        // Transform every watch link in a custom button
        // .replaceAll(
        //   /<rawlink>(https:\/\/tootor\..+?\/watch\/[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12})<\/rawlink>/gi,
        //   (_, $1) => `<a class="chatbot-link" href="${$1}">▶️ Guarda il video</a>`
        // )
        // Transform every email address in a custom button
        // .replaceAll(/\b(\w+?@\w+?\.\w+?)\b/g, (_, $1) => ` <a class="chatbot-link" href="mailto:${$1}" target="_blank">✉️ ${$1}</a> `)
        // Transform every phone number in a custom button
        // .replaceAll(/(\+[\s\d]+\d)(\s)?/g, (_, $1, $2) => `<a class="chatbot-link" href="tel:${$1.replaceAll(/\s/g, '')}">📞 ${$1}</a>${$2 ? ' ' : ''}`)
        // Transform every **Markdown bold** in HTML bold
        .replaceAll(/\*\*(.+?)\*\*/g, (_, $1) => `<b>${$1}</b>`)
        // Remove every space directly between closing and opening tags
        .replaceAll(/>\s*</g, '><')
        // Transform every three or more consecutive <br>s in two <br>s
        .replaceAll(/(<br>){3,}/gi, '<br><br>')
        // Remove every leading and trailing <br>s
        .replaceAll(/(^(<br>)+)|((<br>)+$)/gi, '')
        // Remove any <br>s before, after or between list items
        .replaceAll('</li><br><li>', '</li><li>')
        .replaceAll('<ul><br>', '<ul>')
        .replaceAll('</ul><br>', '</ul>')
        .replaceAll('<br></ul>', '</ul>')
        .replaceAll('<li><br>', '<li>')
        .replaceAll('<ul>', '<ul style="padding-inline-start: 1rem; margin: 0.5rem 0">')
    );
  };

  const reloadButton = (
    <button className="chatbot-link" onClick={closeSession}>
      🔄 Ricarica
    </button>
  );

  if (!canUseChatbot) return <></>;

  return (
    <div className={styles.btnChatbot}>
      <OverlayTrigger
        trigger={'click'}
        placement={'top'}
        overlay={
          <Popover id={'popover-chatbot'} className={'d-flex flex-column'}>
            <Popover.Title as={'h3'}>
              <div className="d-flex justify-content-between text-dark">
                <span>
                  TootorBot{' '}
                  <Badge variant={'light'} className={styles.normalWeight}>
                    alpha
                  </Badge>
                </span>
                <FontAwesomeIcon className={styles.clickable} onClick={close} icon={faTimes} />
              </div>
            </Popover.Title>
            <Popover.Content className={`flex-grow-1 d-flex flex-column position-relative ${styles.coloredPopover} pt-0`}>
              {sessionExpired && (
                <div className={styles.chatbotExpiredSession}>
                  <div className={'mb-2'}>Sessione terminata per inattività.</div>
                  {reloadButton}
                </div>
              )}
              {loading ? (
                <div className={'text-center'}>Caricamento...</div>
              ) : (
                <>
                  <div className={`flex-grow-1 overflow-auto mb-1 d-flex flex-column ${styles.chatScrollable}`} ref={containerRef}>
                    <p className={`text-center pb-2 ${styles.defaultBg} mb-0 ${styles.bgBleedDisclaimer}`}>
                      <small>TootorBot è un'intelligenza artificiale e può commettere errori.</small>
                    </p>

                    {messages.map((m, i) => (
                      <div
                        className={
                          `d-flex ${styles.chatbotBubbleWrapper} ${m.source == 'bot' ? styles.chatbotBubbleBot : styles.chatbotBubbleUser} ${m.thinking ? styles.thinking : ''} ${m.error ? styles.error : ''}`
                        }
                        key={m.id}
                      >
                        {m.source == 'bot' && (
                          <span className={`d-flex flex-column align-items-center ${styles.stickyBottom}`}>
                            {!m.error && !m.thinking && (
                              <WithTooltip text={'Segnala'} big={false}>
                                <FontAwesomeIcon
                                  icon={faFlag}
                                  onClick={async () => {
                                    const url = new URL(`${baseUrl}/sessions/${sessionId}/report`);
                                    url.searchParams.set('messageId', i.toString());
                                    const token = await currentUser.getIdToken(true);
                                    await fetch(url.toString(), {
                                      method: 'POST',
                                      headers: { authorization: token },
                                    });
                                    toast(<div>Operazione completata</div>);
                                  }}
                                   className={`mb-2 ${styles.clickableMessage}`}
                                />
                              </WithTooltip>
                            )}
                            <GravatarChatIcon source={m.source}>{{ user: m.source == 'user' ? currentUser : {} }}</GravatarChatIcon>
                          </span>
                        )}
                        {m.error && <p className={styles.chatbotBubble}>Si è verificato un errore. {reloadButton}</p>}
                        {!m.error && <p className={styles.chatbotBubble} dangerouslySetInnerHTML={{ __html: m.thinking ? ' ' : parse(m.text) }} />}
                      </div>
                    ))}

                    {!warningAccepted && (
                      <Alert variant={'warning'} className={'small'} dismissible={true} onClose={() => setWarningAccepted(true)}>
                        <strong>Attenzione</strong>
                        <div>TootorBot non è un canale di comunicazione con l'assistenza tecnica.</div>
                        <div>Eventuali quesiti tecnici o segnalazioni non riceveranno risposta.</div>
                      </Alert>
                    )}
                  </div>

                  <Form onSubmit={onFormSubmit}>
                    <Form.Group className={'mb-0'}>
                      <Form.Text className={'d-flex justify-content-between pb-2'}>
                        <div>
                          <button className={'chatbot-link mr-1'} onClick={() => onFormSubmit(null, 'Cosa puoi fare?')}>
                            Scopri cosa può fare TootorBot
                          </button>
                          <button className={'chatbot-link'} onClick={() => onFormSubmit(null, 'Cosa mi consigli di guardare?')}>
                            Chiedi consigli su cosa guardare
                          </button>
                        </div>
                      </Form.Text>
                      <Form.Control
                        ref={textRef}
                        as={'textarea'}
                        placeholder={'Scrivi qui il tuo messaggio...'}
                        onKeyDown={(e: any) => {
                          if (e.key == 'Enter') {
                            onFormSubmit();
                            return false;
                          } else if (e.key == 'ArrowUp') {
                            if (!text) {
                              const userMessages = messages.filter((m) => m.source == 'user');
                              if (userMessages.length) {
                                const lastUserMessage = userMessages[userMessages.length - 1];
                                setText(lastUserMessage.text);
                                setTimeout(() => {
                                  textRef.current.selectionStart = lastUserMessage.text.length;
                                });
                                return false;
                              }
                            }
                          }
                          return true;
                        }}
                        rows={3}
                         className={styles.nonResizable}
                        autoFocus
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        disabled={thinking || sessionExpired}
                        size={'sm'}
                        maxLength={maxTextLength}
                      />
                      <Form.Text className={'d-flex justify-content-between'}>
                        {!text.length && (
                          <button className={'chatbot-link danger ml-auto'} onClick={closeSession}>
                            Termina conversazione
                          </button>
                        )}
                        {!!text.length && (
                          <>
                            <span>Premi Invio per inviare il messaggio.</span>
                            <span>
                              {text.length}/{maxTextLength}
                            </span>
                          </>
                        )}
                      </Form.Text>
                    </Form.Group>
                  </Form>
                </>
              )}
            </Popover.Content>
          </Popover>
        }
      >
        <Button variant={'warning'} size={'lg'} ref={buttonRef} onClick={() => setTimeout(scrollToBottom)}>
          <FontAwesomeIcon icon={faRobot} fixedWidth={true} size={'lg'} />
        </Button>
      </OverlayTrigger>
    </div>
  );
};

export { ChatbotComponent as Chatbot };

const GravatarChatIcon = forwardRef(({ children, source, size = 35 }: any, _) => {
  const [url, setUrl] = useState(source == 'user' ? children.user.photoURL : null);

  return (
    <Gravatar email={source == 'user' ? children.user.email || '' : ''} options={{ default: 'mp', size }}>
      {(_url) => (
        <Image
          src={source == 'user' ? url ?? _url : process.env.PUBLIC_URL + '/logo-oo.png'}
          onError={() => setUrl(_url)}
          className={styles.chatAvatar}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            padding: source == 'bot' ? '4px' : '0',
          }}
          roundedCircle
        />
      )}
    </Gravatar>
  );
});
